import type {DefaultError} from "@tanstack/react-query";
import {useMutation} from "@tanstack/react-query";
import {useStores} from "~/stores";
import type {UrlQueryKey} from "~/query/queryClient.ts";
import {queryClient, useCustomQuery} from "~/query/queryClient.ts";
import {queryHttp} from "~/query/queryHttp.ts";
import type {PageResponse} from "~/services/ApiTypes.ts";
import React from "react";
import {contractsUrl} from "./useContractExplorerQueries.ts";

/** The six review states of a contract-transaction match (backend LinkStatus). */
export type ClLinkStatus = 'linked' | 'candidate' | 'accepted' | 'rejected' | 'claimed' | 'competing';

/** The part belongs to this contract in these states. */
export const ACTIVE_LINK_STATUSES: ClLinkStatus[] = ['linked', 'accepted', 'claimed'];

export type CompetingContractRef = {
    contract_id: number;
    contract_title: string;
    confidence: number | null;
    status: ClLinkStatus;
};

export type ContractLinkRow = {
    id: number;
    part_id: number;
    p_id: string;
    date: string | null;
    description: string;
    supplier: string;
    spend: number;
    /** Extra part columns, present only when the caller asked for them. */
    extra?: Record<string, string | number | null>;
    confidence: number | null;
    match_reason: string;
    status: ClLinkStatus;
    contract_item_id: number | null;
    matched_at: string;
    acted_by: string | null;
    acted_at: string | null;
    /** Who else matched the part; `[]` unless this contract owns the row. */
    competing_contracts: CompetingContractRef[];
    /** The contract that owns the part; `null` unless the row is competing. */
    owned_by: CompetingContractRef | null;
};

/** The last match run's context (`GET …/match-status/`), or null when the matcher never ran. */
export type MatchRunContext = {
    contract_supplier_name: string;
    matched_supplier: string | null;
    matched_parent_supplier: string | null;
    supplier_match_level: string;
    contract_entity_names: string[];
    matched_entities: {id: number; name: string}[];
    effective_date: string | null;
    ai_summary: string;
    run_id: number | null;
    matched_at: string | null;
};

type PrerequisiteField = {value: string | null; status: 'not_found' | 'not_matched' | 'matched' | 'found'};

/**
 * `GET …/match-status/` — the matching prerequisites plus the last run's
 * context and the per-status link counts. `POST …/supplier/` and
 * `POST …/entities/` return the same payload.
 */
export type MatchStatusResponse = {
    supplier: PrerequisiteField & {
        matched_supplier: {id: number; name: string} | null;
        matched_parent_supplier: {id: number; name: string} | null;
        match_level: string;
    };
    // contracts are routinely bought by several entities (Hermes emits a list)
    entity: {value: string[]; status: PrerequisiteField['status']; entities: {id: number; name: string}[]};
    effective_date: PrerequisiteField;
    ready_for_matching: boolean;
    context: MatchRunContext | null;
    status_counts: Record<ClLinkStatus, number>;
};

/** One row of `GET /api/data/{bagId}/entities/` (a bare array). */
export type EntityRow = {id: number; name: string; created_at?: string};

export type ExtraPartColumn = {key: string; label: string};

/** Columns the transaction tables already show, so the configured tail never
 *  duplicates them. */
const BASE_PART_COLUMNS = ['p_id', 'p_description', 'p_spend', 'p_ddate_1'];

/**
 * Extra part columns to show, from the databag's columnConfig
 * (`GET config/databag/{id}/`, already loaded into the bag store).
 *
 * Returns nothing unless the customer profile opts in
 * (`contractExplorerConfigColumns`), so by default both surfaces show only
 * their base columns and no `columns=` parameter is sent at all.
 */
export function useExtraPartColumns(): ExtraPartColumn[] {
    const {bagStore, p} = useStores();
    const enabled = Boolean(p.p.contractExplorerConfigColumns);
    const columns = bagStore.apiColumns;
    return React.useMemo(() => {
        if (!enabled) return [];
        return columns
            .filter(column => column.table_name === 'data_source_part'
                && !BASE_PART_COLUMNS.includes(column.column_name))
            .map(column => ({key: column.column_name, label: column.display_name || column.column_name}));
    }, [enabled, columns]);
}

/**
 * The configured extra columns the server actually honoured (its allowlist
 * wins). The page envelope no longer says which, so read it off the first
 * row's `extra` keys; with no rows there is nothing to disagree with, so all
 * configured columns stay.
 */
export function honouredExtraColumns(
    configured: ExtraPartColumn[],
    rows: {extra?: Record<string, unknown>}[],
): ExtraPartColumn[] {
    const first = rows[0];
    if (!first) return configured;
    const present = first.extra ?? {};
    return configured.filter(column => column.key in present);
}

/** One row of the manual-link picker. `link_status` is set when the
 *  transaction is already on this contract (so it cannot be linked again). */
export type LinkCandidateRow = {
    part_id: number;
    p_id: string;
    description: string;
    date: string | null;
    spend: number;
    supplier: string;
    po_number: string;
    link_status: ClLinkStatus | null;
    extra?: Record<string, string | number | null>;
};

export const contractLinksUrl = (bagId: number, contractId: number) =>
    `${contractsUrl(bagId)}${contractId}/links/`;
export const matchStatusUrl = (bagId: number, contractId: number) =>
    `${contractsUrl(bagId)}${contractId}/match-status/`;
export const entitiesUrl = (bagId: number) => `/data/${bagId}/entities/`;
export const linkCandidatesUrl = (bagId: number, contractId: number) =>
    `${contractsUrl(bagId)}${contractId}/link-candidates/`;

/** `GET …/links/` — every link of the contract in one DRF page (`limit: 500`). */
export function useContractLinksQuery(bagId: number, contractId: number, columns: string[] = []) {
    return useCustomQuery<PageResponse<ContractLinkRow>, DefaultError, PageResponse<ContractLinkRow>, UrlQueryKey>({
        queryKey: [contractLinksUrl(bagId, contractId), {params: {
            limit: 500,
            ...columns.length > 0 && {columns: columns.join(',')},
        }}],
        enabled: bagId > 0 && contractId > 0,
        staleTime: 30_000,
    });
}

export function useMatchStatusQuery(bagId: number, contractId: number) {
    return useCustomQuery<MatchStatusResponse, DefaultError, MatchStatusResponse, UrlQueryKey>({
        queryKey: [matchStatusUrl(bagId, contractId)],
        enabled: bagId > 0 && contractId > 0,
        staleTime: 30_000,
    });
}

/** Transactions the reviewer can link by hand (`GET …/link-candidates/`, one
 *  DRF page of the default size, `count` = the true total). Only runs while
 *  the picker is open, so the tab costs nothing until then. */
export function useLinkCandidatesQuery(
    bagId: number,
    contractId: number,
    search: string,
    allSuppliers: boolean,
    enabled: boolean,
    columns: string[] = [],
) {
    return useCustomQuery<PageResponse<LinkCandidateRow>, DefaultError, PageResponse<LinkCandidateRow>, UrlQueryKey>({
        queryKey: [linkCandidatesUrl(bagId, contractId), {params: {
            ...search && {search},
            ...allSuppliers && {all_suppliers: 'true'},
            ...columns.length > 0 && {columns: columns.join(',')},
        }}],
        enabled: enabled && bagId > 0 && contractId > 0,
        staleTime: 30_000,
    });
}

export function useEntitiesQuery(bagId: number, enabled: boolean) {
    return useCustomQuery<EntityRow[], DefaultError, EntityRow[], UrlQueryKey>({
        queryKey: [entitiesUrl(bagId)],
        enabled: enabled && bagId > 0,
        staleTime: 60_000,
    });
}

/**
 * A review action changes more than the row: ownership can flip on *another*
 * contract, counts move, and the first action freezes re-parses (`has_review`)
 * on the detail. Cheapest correct move: drop every links / match-status /
 * link-candidates / insights / detail query of this databag and let the open
 * ones refetch (the candidates carry `link_status`, which the action changed).
 */
function invalidateMatching(bagId: number) {
    void queryClient.invalidateQueries({
        predicate: query => {
            const url = String(query.queryKey[0] ?? '');
            // the /\d+\/$ arm is the contract detail (has_review changes)
            return url.startsWith(contractsUrl(bagId)) && (
                url.endsWith('/links/') || url.endsWith('/match-status/') || url.endsWith('/link-candidates/')
                || url.includes('/insights/') || /\/\d+\/$/.test(url)
            );
        },
    });
}

type LinkActionVariables = {bagId: number; contractId: number; linkId: number; action: 'accept' | 'reject' | 'claim'};

export function useLinkActionMutation() {
    return useMutation<ContractLinkRow, DefaultError, LinkActionVariables>({
        mutationFn: async ({bagId, contractId, linkId, action}) => {
            const {data} = await queryHttp.post<ContractLinkRow>(
                `${contractLinksUrl(bagId, contractId)}${linkId}/action/`, {action},
            );
            return data;
        },
        onSuccess: (_data, {bagId}) => invalidateMatching(bagId),
    });
}

export function useManualLinkMutation() {
    return useMutation<ContractLinkRow, DefaultError, {bagId: number; contractId: number; partId: number}>({
        mutationFn: async ({bagId, contractId, partId}) => {
            const {data} = await queryHttp.post<ContractLinkRow>(
                `${contractsUrl(bagId)}${contractId}/links/manual/`, {part_id: partId},
            );
            return data;
        },
        onSuccess: (_data, {bagId}) => invalidateMatching(bagId),
    });
}

export function useSetSupplierMutation() {
    return useMutation<MatchStatusResponse, DefaultError,
        {bagId: number; contractId: number; level: 'supplier' | 'parent'; name: string}>({
        mutationFn: async ({bagId, contractId, level, name}) => {
                const {data} = await queryHttp.post<MatchStatusResponse>(
                    `${contractsUrl(bagId)}${contractId}/supplier/`, {level, name},
                );
                return data;
            },
        onSuccess: (data, {bagId, contractId}) => {
                queryClient.setQueryData([matchStatusUrl(bagId, contractId)], data);
                void queryClient.invalidateQueries({queryKey: [contractLinksUrl(bagId, contractId)]});
                // The candidates default to the matched supplier's transactions,
                // so a new supplier changes what the picker should offer.
                void queryClient.invalidateQueries({queryKey: [linkCandidatesUrl(bagId, contractId)]});
            },
    });
}

export type RunMatchingResult = {
    /** The prerequisites only — no run context or status counts here. */
    prerequisites: Omit<MatchStatusResponse, 'context' | 'status_counts'>;
    matches: number;
    transactions: number;
    summary: string;
};

/**
 * Find this contract's transactions now.
 *
 * The request runs the matcher inline — LLM calls, so seconds rather than
 * milliseconds — hence the raised timeout. Everything it writes lands through
 * the normal ingest, so the usual invalidation refreshes the panel.
 */
export function useRunMatchingMutation() {
    return useMutation<RunMatchingResult, DefaultError, {bagId: number; contractId: number}>({
        mutationFn: async ({bagId, contractId}) => {
            const {data} = await queryHttp.post<RunMatchingResult>(
                `${contractsUrl(bagId)}${contractId}/run-matching/`, {}, {timeout: 5 * 60 * 1000},
            );
            return data;
        },
        onSuccess: (_data, {bagId}) => invalidateMatching(bagId),
    });
}

export function useSetEntitiesMutation() {
    return useMutation<MatchStatusResponse, DefaultError, {bagId: number; contractId: number; entityIds: number[]}>({
        mutationFn: async ({bagId, contractId, entityIds}) => {
            const {data} = await queryHttp.post<MatchStatusResponse>(
                `${contractsUrl(bagId)}${contractId}/entities/`, {entity_ids: entityIds},
            );
            return data;
        },
        onSuccess: (data, {bagId, contractId}) => {
            queryClient.setQueryData([matchStatusUrl(bagId, contractId)], data);
            void queryClient.invalidateQueries({queryKey: [contractLinksUrl(bagId, contractId)]});
        },
    });
}
